import React, { useCallback, useEffect, useRef, useState } from 'react'
import { getYear } from 'date-fns'
import { Container, Header, TableContainer, Year, YearContainer } from './styles'
import { ISelectOption, Input, Select, Table } from '~/components/shared'
import { message } from '~/services'
import { usePatient } from '~/context'
import { useVaccines } from '~/context/vaccines'
import { IVaccine, IVaccineParams } from '~/interfaces'
import { transformDate } from '~/utils'
import { useNormalizedFilter } from '~/hooks'

export const Vaccines = (): JSX.Element => {
  const inputFilter = useRef<HTMLInputElement>(null)
  const [allowRefresh, setAllowRefresh] = useState<boolean>(false)
  const [messageError, setMessageError] = useState<string>('')
  const [selectedYear, setSelectedYear] = useState<string | 'Todos'>('Todos')
  const [name, setName] = useState<string>('')
  const [filteredData, setFilteredData] = useState<IVaccine[]>([])

  const { filter } = useNormalizedFilter()
  const { isLoading, getVaccines, vaccines, years } = useVaccines()
  const { getVisiblePatientId } = usePatient()
  const patientId = getVisiblePatientId()

  const fetchData = useCallback(
    async (filter: IVaccineParams) => {
      try {
        await getVaccines({ ...filter, patientId })
      } catch (error) {
        setAllowRefresh(true)
        setMessageError(message('timeline.error'))
      }
    },
    [getVaccines, patientId]
  )

  const handleSearch = useCallback(() => {
    setAllowRefresh(true)
    const dataToFilter =
      selectedYear !== 'Todos'
        ? vaccines.filter(d => getYear(Date.parse(d.date)).toString() === selectedYear)
        : vaccines

    if (name)
      setFilteredData(
        filter(dataToFilter, {
          searchTerm: name,
          filterKey: 'name'
        })
      )
    else {
      setFilteredData(dataToFilter)
    }
  }, [name, selectedYear, filter, vaccines])

  useEffect(() => {
    if (patientId) fetchData({})
  }, [patientId])

  useEffect(() => {
    setFilteredData(vaccines)
  }, [vaccines])

  useEffect(() => {
    setMessageError(allowRefresh ? message('timeline.result_search_empty') : message('timeline.data_not_found'))
  }, [allowRefresh])

  const parseVaccinesToRowData = (vaccines: IVaccine[]): IVaccine[] => {
    return vaccines.map(({ date, name }) => {
      return {
        name,
        date: transformDate({ date })
      } as IVaccine
    })
  }

  useEffect(() => {
    if (selectedYear !== 'Todos' || name) handleSearch()
    else setFilteredData(vaccines)
  }, [selectedYear, name])

  const handleRefresh = () => {
    setSelectedYear('Todos')
    setName('')
    inputFilter.current!.value = ''
  }

  return (
    <Container>
      <Header>
        {years.length > 0 && (
          <YearContainer>
            <Year>Ano</Year>
            <Select
              name="select"
              placeholder={message('timeline.select_year_placeholder')}
              maxHeight="250px"
              setValue={value => setSelectedYear(value ? value.toString() : '')}
              value={selectedYear}
              options={[{ label: 'Todos', value: 'Todos' } as ISelectOption].concat(years)}
            />
          </YearContainer>
        )}
        <Input
          icon="search"
          ref={inputFilter}
          name="search"
          placeholder={message('timeline.search_placeholder')}
          onChange={input => setName(input.target.value)}
        />
      </Header>
      <TableContainer>
        <Table
          isLoading={isLoading}
          onRefresh={allowRefresh ? handleRefresh : undefined}
          data={parseVaccinesToRowData(filteredData)}
          headerLineHeight="30px"
          headerPadding="10px 0 10px 24px"
          minHeight="0px"
          messageError={messageError}
          model={[
            {
              key: 'date',
              label: message('timeline.date').toUpperCase(),
              width: '18%',
              sortable: true
            },
            {
              key: 'name',
              label: message('timeline.vaccine').toUpperCase(),
              sortable: true,
              minWidth: '300px'
            }
          ]}
        />
      </TableContainer>
    </Container>
  )
}
